import React, { useState, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../index";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./EmployeeForm.module.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// SVG Icon for the delete button
const DeleteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    width="20px"
    height="20px"
  >
    <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM13.4142 13.9997L15.182 15.7675L13.7678 17.1817L12 15.4139L10.2322 17.1817L8.81802 15.7675L10.5858 13.9997L8.81802 12.232L10.2322 10.8178L12 12.5855L13.7678 10.8178L15.182 12.232L13.4142 13.9997ZM15 4H9V6H15V4Z"></path>
  </svg>
);

const EmployeeForm = () => {
  const { t, i18n } = useTranslation();
  const { employeeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  const isLaborer = location.pathname.includes("/laborers");
  const isEditMode = !!employeeId;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firebaseId: null,
    accountType: isLaborer ? "non_permanent" : "permanent",
    basicSalary: "",
    rate: "",
    rateType: "hourly", // Default to "Hourly" as per requirements
    roleId: "",
    scope: "farm", // Default to "This Farm" as per requirements
    hasLogin: isLaborer ? false : true, // UPDATED: Laborers don't have login by default
    workersIds: [],
    existingEmployeeId: "", // For adding an existing user
    supervisorId: "",
  });

  const [roles, setRoles] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [existingUsers, setExistingUsers] = useState([]);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [allTenantFarms, setAllTenantFarms] = useState([]);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);

  const [customRoleData, setCustomRoleData] = useState(null);
  const [editModeRoleName, setEditModeRoleName] = useState("");
  const [supervisors, setSupervisors] = useState([]);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const selectedRole = roles.find((r) => r.id === parseInt(formData.roleId));
  const isSupervisorRole = selectedRole?.nameEn === "Supervisor";
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  console.log("initial data loaded", isInitialDataLoaded);

  // ===== STATE MANAGEMENT HELPERS =====
  // Centralized function to build navigation state object
  const buildNavigationState = (additionalState = {}) => ({
    preservedState: {
      formData,
      selectedFieldIds,
      fields,
      roles,
      workers,
      supervisors,
      existingUsers,
      allTenantFarms,
      isInitialDataLoaded,
      customRoleData,
      isExistingUser,
    },
    ...additionalState,
  });

  // Centralized function to restore state from navigation
  const restoreNavigationState = (state) => {
    if (!state || !state.preservedState) return false;

    const preserved = state.preservedState;

    // Restore all preserved state
    if (preserved.formData) {
      setFormData(preserved.formData);
      if (preserved.formData.existingEmployeeId) {
        setIsExistingUser(true);
      }
    }
    if (preserved.selectedFieldIds) setSelectedFieldIds(preserved.selectedFieldIds);
    if (preserved.fields) setFields(preserved.fields);
    if (preserved.roles) setRoles(preserved.roles);
    if (preserved.workers) setWorkers(preserved.workers);
    if (preserved.supervisors) setSupervisors(preserved.supervisors);
    if (preserved.existingUsers) setExistingUsers(preserved.existingUsers);
    if (preserved.allTenantFarms) setAllTenantFarms(preserved.allTenantFarms);
    if (preserved.isInitialDataLoaded) setIsInitialDataLoaded(preserved.isInitialDataLoaded);
    if (preserved.customRoleData) setCustomRoleData(preserved.customRoleData);
    if (preserved.isExistingUser !== undefined) setIsExistingUser(preserved.isExistingUser);

    return true;
  };
  // --- EFFECT TO HANDLE STATE RESTORATION ---
  //   useEffect(() => {
  //     const state = location.state || {};
  //     let stateWasProcessed = false;

  //     console.log("state", state);
  //     if (state.isInitialDataLoaded) {
  //       console.log("initial data was set to true");
  //       setIsInitialDataLoaded(state.isInitialDataLoaded);
  //       stateWasProcessed = true;
  //     }

  //     if (state.preservedFormData) {
  //       setFormData(state.preservedFormData);
  //       if (state.preservedFormData.existingEmployeeId) {
  //         setIsExistingUser(true);
  //       }
  //       stateWasProcessed = true;
  //     }
  //     if (state.preservedSelectedFieldIds) {
  //       setSelectedFieldIds(state.preservedSelectedFieldIds);
  //       stateWasProcessed = true;
  //     }
  //     if (state.updatedSelection) {
  //       setSelectedFieldIds(state.updatedSelection);
  //       setFormData((prev) => ({ ...prev, scope: "field" }));
  //       stateWasProcessed = true;
  //     }
  //     if (state.updatedRoleData) {
  //       setCustomRoleData(state.updatedRoleData);
  //       setFormData((prev) => ({
  //         ...prev,
  //         roleId: state.updatedRoleData.roleId,
  //       }));
  //       stateWasProcessed = true;
  //     } else if (state.selectedRoleId) {
  //       setFormData((prev) => ({ ...prev, roleId: state.selectedRoleId }));
  //       setCustomRoleData(null);
  //       stateWasProcessed = true;
  //     }
  //     if (state.updatedWorkersSelection) {
  //       setFormData((prev) => ({
  //         ...prev,
  //         workersIds: state.updatedWorkersSelection,
  //       }));
  //       stateWasProcessed = true;
  //     }

  //     if (stateWasProcessed) {
  //       navigate(location.pathname, { replace: true, state: {} });
  //     }
  //   }, [location.state, navigate, location.pathname]);

  //   useEffect(() => {
  //     const fetchData = async () => {
  //       if (roles.length > 0 || !userData?.Tenant?.id) return;
  //       setLoading(true);
  //       try {
  //         const farmId = userData.Farm?.id || 1;
  //         const tenantId = userData.Tenant.id;

  //         const rolesPromise = axios.get(`/api/role/findByTenantId/${tenantId}`, {
  //           headers: { Authorization: token },
  //         });
  //         const employeesPromise = axios.get(
  //           `/api/emp/findByTenantId/${tenantId}`,
  //           { headers: { Authorization: token } }
  //         );
  //         const workersPromise = axios.get(
  //           `/api/emp/getUnsupervisedEmployees/${tenantId}`,
  //           { headers: { Authorization: token } }
  //         );
  //         const fieldsPromise = axios.get(`/api/field/findByFarmId/${farmId}`, {
  //           headers: { Authorization: token },
  //         });
  //         const farmsPromise = axios.get(`/api/farm/findByTenantId/${tenantId}`, {
  //           headers: { Authorization: token },
  //         });
  //         const supervisorsPromise = axios.get(
  //           `/api/emp/getSupervisors/${tenantId}`,
  //           {
  //             headers: { Authorization: token },
  //           }
  //         );

  //         const [
  //           rolesResponse,
  //           employeesResponse,
  //           fieldsResponse,
  //           workersResponse,
  //           farmsResponse,
  //           supervisorsResponse,
  //         ] = await Promise.all([
  //           rolesPromise,
  //           employeesPromise,
  //           fieldsPromise,
  //           workersPromise,
  //           farmsPromise,
  //           supervisorsPromise,
  //         ]);

  //         console.log("supervisors list", supervisorsResponse.data.items);
  //         setSupervisors(supervisorsResponse.data.items || []);
  //         console.log("supervisors list from variable", supervisors);
  //         const flattenedRoles = (rolesResponse.data.items || [])
  //           .filter((item) => item && item.Role)
  //           .map((item) => ({
  //             id: item.Role.id,
  //             nameEn: item.Role.nameEn,
  //             nameAr: item.Role.nameAr,
  //           }));
  //         setRoles(flattenedRoles);

  //         // NEW: Automatically set role to "Worker" for new laborers.
  //         if (isLaborer && !isEditMode) {
  //           const workerRole = flattenedRoles.find(
  //             (role) => role.nameEn.toLowerCase() === "worker"
  //           );
  //           if (workerRole) {
  //             setFormData((prev) => ({ ...prev, roleId: workerRole.id }));
  //           }
  //         }

  //         setFields(fieldsResponse.data.items || []);
  //         setAllTenantFarms(farmsResponse.data.items || []);
  //         const allTenantEmployees = employeesResponse.data.items || [];
  //         setWorkers(workersResponse.data.items || []);
  //         const usersToAdd = allTenantEmployees.filter(
  //           (emp) =>
  //             emp.Employee?.farmId !== farmId &&
  //             emp.Employee?.accountType === "permanent"
  //         );
  //         setExistingUsers(usersToAdd);

  //         // extended population of the form when editing that includes all data
  //         // if (isEditMode && employeeId && !isInitialDataLoaded) {
  //         //   // 1. Fetch the entire list of employees for the tenant
  //         //   const empResponse = await axios.get(
  //         //     `/api/emp/findByTenantId/${userData.Tenant.id}`,
  //         //     {
  //         //       headers: { Authorization: token },
  //         //     }
  //         //   );

  //         //   const allTenantEmployees = empResponse.data.items || [];

  //         //   // 2. Find the specific employee from the list using the employeeId from the URL
  //         //   // We use parseInt because the employeeId from the URL is a string
  //         //   const empData = allTenantEmployees.find(
  //         //     (emp) => emp.Employee && emp.Employee.id === parseInt(employeeId)
  //         //   );

  //         //   // 3. Populate the form state with the found employee's data
  //         //   if (empData) {
  //         //     setFormData((prev) => ({
  //         //       ...prev,
  //         //       username: empData.Employee.username,
  //         //       email: empData.User?.email || "",
  //         //       firebaseId: empData.User?.firebaseId || null,
  //         //       accountType: empData.Employee.accountType,
  //         //       basicSalary: empData.Employee.basicSalary || "",
  //         //       rate: empData.Employee.rate || "",
  //         //       rateType: empData.Employee.rateType || "hourly",
  //         //       hasLogin: empData.Employee.hasLogin,
  //         //       roleId: empData.EmployeeRoles?.[0]?.roleId || "",
  //         //       scope: empData.EmployeeRoles?.[0]?.scope || "farm",
  //         //       supervisorId: empData.Employee.supervisorId || "",
  //         //     }));

  //         //     // This logic correctly extracts the field IDs if the scope is "field"
  //         //     const fieldIds = (empData.EmployeeRoles || [])
  //         //       .filter((er) => er.scope === "field" && er.fieldId)
  //         //       .map((er) => er.fieldId);
  //         //     setSelectedFieldIds(fieldIds);
  //         //  setIsInitialDataLoaded(true);
  //         //   }
  //         // }

  //         // a sammurized version of the population
  //         if (isEditMode && employeeId && !isInitialDataLoaded) {
  //           // This uses the GET emp/{id} endpoint which returns the flat object you provided.
  //           console.log("data updated in edit mode");
  //           console.log("initial data loaded flag:", isInitialDataLoaded);
  //           const empResponse = await axios.get(`/api/emp/${employeeId}`, {
  //             headers: { Authorization: token },
  //           });

  //           const empData = empResponse.data.items?.[0];
  //           setEditModeRoleName(empData.role || "");
  //           if (empData) {
  //             setFormData((prev) => ({
  //               ...prev,
  //               // --- Fields Populated from API ---
  //               username: empData.username || "", //
  //               email: empData.email || "", //
  //               basicSalary: empData.basicSalary || "", //

  //               // --- Fields Missing from API (set to default) ---
  //               // See warning below. This will cause data loss on save.
  //               firebaseId: null,
  //               accountType: "permanent", // Defaulting, as this is missing from the API response
  //               rate: "",
  //               rateType: "hourly",
  //               hasLogin: true, // Defaulting, as this is missing from the API response
  //               roleId: "", // MISSING: The API sends a role NAME, but not the required ID.
  //               scope: "farm", // MISSING: The API does not send the scope.
  //               supervisorId: "", // MISSING: The API does not send the supervisorId.
  //             }));

  //             // Set field permissions to empty as this data is missing from the API response
  //             setSelectedFieldIds([]);
  //             setIsInitialDataLoaded(true);
  //           }
  //         }
  //       } catch (error) {
  //         toast.error(t("Could not load initial form data."));
  //         console.error("Fetch Error:", error);
  //       } finally {
  //         setLoading(false);
  //       }
  //     };

  //     fetchData();
  //   }, [
  //     isEditMode,
  //     employeeId,
  //     token,
  //     userData?.Tenant?.id,
  //     userData?.Farm?.id,
  //     roles.length,
  //     t,
  //     isLaborer,
  //     supervisors,
  //     isInitialDataLoaded,
  //   ]);
  // FILE: EmployeeForm.js

  // --- NEW: COMBINED EFFECT FOR INITIAL LOAD AND STATE RESTORATION ---
  // --- COMBINED EFFECT FOR INITIAL LOAD AND STATE RESTORATION ---
  useEffect(() => {
    // --- Part 1: Handle State Restoration from Navigation ---
    const state = location.state || {};
    let stateWasProcessed = false;

    // Restore preserved state using helper function
    if (restoreNavigationState(state)) {
      stateWasProcessed = true;
    }

    // Handle specific updates from selector pages
    if (state.fieldSelection !== undefined) {
      setSelectedFieldIds(state.fieldSelection);
      setFormData((prev) => ({ ...prev, scope: "field" }));
      stateWasProcessed = true;
    }
    if (state.roleData) {
      setCustomRoleData(state.roleData);
      setFormData((prev) => ({
        ...prev,
        roleId: state.roleData.roleId,
      }));
      stateWasProcessed = true;
    } else if (state.roleId !== undefined) {
      setFormData((prev) => ({ ...prev, roleId: state.roleId }));
      setCustomRoleData(null);
      stateWasProcessed = true;
    }
    if (state.workerSelection !== undefined) {
      setFormData((prev) => ({
        ...prev,
        workersIds: state.workerSelection,
      }));
      stateWasProcessed = true;
    }

    // If any state was restored, clear location.state and exit early.
    if (stateWasProcessed) {
      navigate(location.pathname, { replace: true, state: {} });
      return; // IMPORTANT: Prevents data fetching after state restoration.
    }

    // --- Part 2: Handle Initial Data Fetch (if not restoring state) ---
    const fetchData = async () => {
      setLoading(true);
      try {
        const farmId = userData.Farm?.id || 1;
        const tenantId = userData.Tenant.id;

        // Fetch all initial data in parallel
        const [
          rolesResponse,
          employeesResponse,
          workersResponse,
          fieldsResponse,
          farmsResponse,
          supervisorsResponse,
        ] = await Promise.all([
          axios.get(`/api/role/findByTenantId/${tenantId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`/api/emp/findByTenantId/${tenantId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`/api/emp/getUnsupervisedEmployees/${tenantId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`/api/field/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`/api/farm/findByTenantId/${tenantId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`/api/emp/getSupervisors/${tenantId}`, {
            headers: { Authorization: token },
          }),
        ]);

        const flattenedRoles = (rolesResponse.data.items || [])
          .filter((item) => item && item.Role)
          .map((item) => ({
            id: item.Role.id,
            nameEn: item.Role.nameEn,
            nameAr: item.Role.nameAr,
          }));
        setRoles(flattenedRoles);
        setSupervisors(supervisorsResponse.data.items || []);
        console.log("fields", fieldsResponse.data.items);
        setFields(fieldsResponse.data.items || []);
        setAllTenantFarms(farmsResponse.data.items || []);
        setWorkers(workersResponse.data.items || []);
        const allTenantEmployees = employeesResponse.data.items || [];
        const usersToAdd = allTenantEmployees.filter((emp) => {
          // Check if employee is permanent
          if (emp.Employee?.accountType !== "permanent") return false;

          // Check if employee is already in this farm
          // First check primary farmId
          if (emp.Employee?.farmId === farmId) return false;

          // Then check all associated farms via EmployeeRoles
          const isInCurrentFarm = emp.EmployeeRoles?.some(
            (role) => role.farmId === farmId
          );

          return !isInCurrentFarm;
        });
        setExistingUsers(usersToAdd);

        // Logic for Create Mode
        if (!isEditMode) {
          if (isLaborer) {
            const workerRole = flattenedRoles.find(
              (role) => role.nameEn.toLowerCase() === "worker"
            );
            if (workerRole) {
              setFormData((prev) => ({ ...prev, roleId: workerRole.id }));
            }
          }
        }
        // Logic for Edit Mode
        else if (isEditMode && employeeId) {
          const empResponse = await axios.get(`/api/emp/${employeeId}`, {
            headers: { Authorization: token },
          });
          const empData = empResponse.data.items?.[0];

          if (empData) {
            setEditModeRoleName(empData.role || "");
            const roleNameFromApi = empData.role;
            const matchingRole = flattenedRoles.find(
              (r) =>
                r.nameEn === roleNameFromApi || r.nameAr === roleNameFromApi
            );
            const roleIdToSet = matchingRole ? matchingRole.id : "";

            setFormData((prev) => ({
              ...prev,
              username: empData.username || "",
              email: empData.email || "",
              basicSalary: empData.basicSalary || "",
              accountType: "permanent",
              roleId: roleIdToSet,
              firebaseId: null,
              rate: "",
              rateType: "hourly",
              hasLogin: true,
              scope: "farm",
              supervisorId: "",
            }));
            setSelectedFieldIds([]);
          }
        }
      } catch (error) {
        toast.error(t("Could not load initial form data."));
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
        setIsInitialDataLoaded(true); // Set flag after all fetching is done
      }
    };

    // Only run the fetch logic if we haven't loaded data yet and have the tenant ID
    if (!isInitialDataLoaded && userData?.Tenant?.id) {
      fetchData();
    } else if (!userData?.Tenant?.id) {
      setLoading(true); // Show loading until user context is ready
    } else {
      setLoading(false); // If data is loaded, stop loading indicator
    }
  }, [
    employeeId,
    isEditMode,
    isLaborer,
    isInitialDataLoaded,
    location.state,
    location.pathname, // Added as it's used in navigate
    navigate,
    token,
    t,
    userData,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRemoveWorker = (workerIdToRemove) => {
    setFormData((prev) => ({
      ...prev,
      workersIds: prev.workersIds.filter((id) => id !== workerIdToRemove),
    }));
  };

  const selectedWorkers = workers.filter((worker) =>
    formData.workersIds.includes(worker.id)
  );

  const handleExistingUserToggle = (checked) => {
    setIsExistingUser(checked);
    setFormData((prev) => ({
      ...prev,
      username: "",
      email: "",
      password: "",
      existingEmployeeId: "",
    }));
  };

  const handleExistingUserSelect = (e) => {
    const selectedId = e.target.value;
    const selected = existingUsers.find(
      (u) => u.Employee.id === parseInt(selectedId)
    );
    if (selected) {
      setFormData((prev) => ({
        ...prev,
        existingEmployeeId: selectedId,
        username: selected.Employee.username,
        email: selected.User?.email || "",
      }));
    }
  };

  // FILE: EmployeeForm.js

  // FILE: EmployeeForm.js

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.scope === "field" && selectedFieldIds.length === 0) {
      toast.error(t("Please select at least one field when scope is Fields."));
      return;
    }

    setLoading(true);

    if (isEditMode) {
      // --- UPDATE LOGIC (Mirrors Android EmployeeMapper.java) ---
      const updatePayload = {
        employeeId: parseInt(employeeId),
        username: formData.username,
        basicSalary: parseFloat(formData.basicSalary) || null,
        scope: formData.scope.toLowerCase(),
        tenantId: userData.Tenant.id,
        accountType: formData.accountType,
        farms: [], // Will be populated below
      };

      // Add password fields only if a new password is provided
      if (formData.password) {
        updatePayload.email = formData.email;
        updatePayload.newPassword = formData.password;
        updatePayload.isEmail = true;
      }

      // Add role information based on whether it's a new custom role,
      // an update to a custom role, or a default role.
      if (customRoleData) {
        if (customRoleData.roleId === 0) {
          // Creating a new custom role
          updatePayload.roleId = null;
          updatePayload.roleNameAr = customRoleData.roleNameAr;
          updatePayload.roleNameEn = customRoleData.roleNameEn;
          updatePayload.permissions = customRoleData.permissions;
        } else {
          // Updating an existing custom role
          updatePayload.roleId = customRoleData.roleId;
          updatePayload.updateRole = true;
          updatePayload.roleNameAr = customRoleData.roleNameAr;
          updatePayload.roleNameEn = customRoleData.roleNameEn;
          updatePayload.permissions = customRoleData.permissions;
        }
      } else {
        // Using a default role
        updatePayload.roleId = formData.roleId
          ? parseInt(formData.roleId)
          : null;
      }

      // Add rate fields for non-permanent employees (laborers)
      if (formData.accountType === "non_permanent") {
        updatePayload.rate = parseFloat(formData.rate) || null;
        updatePayload.rateType = formData.rateType;
      }

      // Construct the 'farms' array based on scope
      const farmId = userData.Farm?.id || 1;
      if (formData.scope === "tenant") {
        updatePayload.farms = allTenantFarms.map((farm) => ({
          farmId: farm.id,
        }));
      } else if (formData.scope === "farm") {
        updatePayload.farms = [{ farmId }];
      } else if (formData.scope === "field") {
        updatePayload.farms = selectedFieldIds.map((fieldId) => ({
          farmId,
          fieldId,
        }));
      }

      try {
        await axios.put("/api/emp", updatePayload, {
          headers: { Authorization: token },
        });
        toast.success(t("User updated successfully!"));
        navigate("/employees");
      } catch (error) {
        toast.error(t("Failed to update user."));
        console.error("Update Error:", error.response?.data || error);
      } finally {
        setLoading(false);
      }
    } else {
      // --- CREATE LOGIC (This part remains the same) ---
      // ... your existing create logic ...
      let newEmployeeFirebaseId = null;

      if (formData.hasLogin && !isExistingUser) {
        try {
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            formData.email,
            formData.password
          );
          newEmployeeFirebaseId = userCredential.user.uid;
        } catch (firebaseError) {
          toast.error(`Firebase error: ${firebaseError.message}`);
          console.error("Firebase user creation failed:", firebaseError);
          setLoading(false);
          return;
        }
      }

      const farmId = userData.Farm?.id || 1;
      let farmsPayload = [];
      if (formData.scope === "tenant") {
        farmsPayload = allTenantFarms.map((farm) => ({ farmId: farm.id }));
      } else if (formData.scope === "farm") {
        farmsPayload = [{ farmId: farmId }];
      } else if (formData.scope === "field") {
        farmsPayload = selectedFieldIds.map((fieldId) => ({ farmId, fieldId }));
      }

      let accountSpecialityName = isLaborer
        ? "Worker"
        : selectedRole
        ? i18n.language === "ar"
          ? selectedRole.nameAr
          : selectedRole.nameEn
        : "";
      if (customRoleData) {
        accountSpecialityName =
          i18n.language === "ar"
            ? customRoleData.roleNameAr
            : customRoleData.roleNameEn;
      }

      const createPayload = {
        employeeId: isExistingUser
          ? parseInt(formData.existingEmployeeId)
          : null,
        email: formData.email,
        firebaseId: newEmployeeFirebaseId,
        username: formData.username,
        accountType: formData.accountType,
        accountSpeciality: accountSpecialityName,
        basicSalary:
          formData.accountType === "permanent"
            ? parseFloat(formData.basicSalary)
            : null,
        rate:
          formData.accountType === "non_permanent"
            ? parseFloat(formData.rate)
            : null,
        rateType:
          formData.accountType === "non_permanent" ? formData.rateType : null,
        tenantId: userData.Tenant.id,
        supervisorId: formData.supervisorId
          ? parseInt(formData.supervisorId)
          : null,
        hasLogin: formData.hasLogin,
        scope: formData.scope,
        farms: farmsPayload,
        employeeIdsToUpdate: isSupervisorRole ? formData.workersIds : [],
        permissions: customRoleData ? customRoleData.permissions : [],
        roleId: customRoleData
          ? customRoleData.roleId === 0
            ? null
            : customRoleData.roleId
          : formData.roleId
          ? parseInt(formData.roleId)
          : null,
        roleNameEn: customRoleData
          ? customRoleData.roleNameEn
          : selectedRole
          ? selectedRole.nameEn
          : "",
        roleNameAr: customRoleData
          ? customRoleData.roleNameAr
          : selectedRole
          ? selectedRole.nameAr
          : "",
      };

      try {
        await axios.post("/api/emp/newAddEmpToFarm", createPayload, {
          headers: { Authorization: token },
        });
        toast.success(t("User added successfully!"));
        navigate("/employees");
      } catch (error) {
        toast.error(t("Failed to save user."));
        console.error("Save Error:", error.response?.data || error);
      } finally {
        setLoading(false);
      }
    }
  };

  //   const handleSubmit = async (e) => {
  //     e.preventDefault();

  //     // NEW: Validation for "Fields" scope.
  //     if (formData.scope === "field" && selectedFieldIds.length === 0) {
  //       toast.error(t("Please select at least one field when scope is Fields."));
  //       return; // Stop submission
  //     }

  //     let newEmployeeFirebaseId = null;

  //     if (formData.hasLogin && !isEditMode && !isExistingUser) {
  //       try {
  //         const userCredential = await createUserWithEmailAndPassword(
  //           auth,
  //           formData.email,
  //           formData.password
  //         );
  //         newEmployeeFirebaseId = userCredential.user.uid;
  //       } catch (firebaseError) {
  //         toast.error(`Firebase error: ${firebaseError.message}`);
  //         console.error("Firebase user creation failed:", firebaseError);
  //         return;
  //       }
  //     }

  //     setLoading(true);

  //     const farmId = userData.Farm?.id || 1;
  //     let farmsPayload = [];
  //     if (formData.scope === "tenant") {
  //       farmsPayload = allTenantFarms.map((farm) => ({ farmId: farm.id }));
  //       if (farmsPayload.length === 0) {
  //         farmsPayload = [{ farmId: farmId }];
  //       }
  //     } else if (formData.scope === "farm") {
  //       farmsPayload = [{ farmId: farmId }];
  //     } else if (formData.scope === "field") {
  //       farmsPayload = selectedFieldIds.map((fieldId) => ({
  //         farmId: farmId,
  //         fieldId: fieldId,
  //       }));
  //     }

  //     // UPDATED: Logic to correctly determine account specialty name, including "Worker"
  //     let accountSpecialityName = "Worker"; // Default for laborers
  //     if (!isLaborer) {
  //       if (customRoleData) {
  //         accountSpecialityName =
  //           i18n.language === "ar"
  //             ? customRoleData.roleNameAr
  //             : customRoleData.roleNameEn;
  //       } else if (selectedRole) {
  //         accountSpecialityName =
  //           i18n.language === "ar" ? selectedRole.nameAr : selectedRole.nameEn;
  //       }
  //     }

  //     const payload = {
  //       employeeId: isEditMode
  //         ? parseInt(employeeId)
  //         : isExistingUser
  //         ? parseInt(formData.existingEmployeeId)
  //         : null,
  //       email: formData.email,
  //       firebaseId: isEditMode ? formData.firebaseId : newEmployeeFirebaseId,
  //       username: formData.username,
  //       accountType: formData.accountType,
  //       accountSpeciality: accountSpecialityName,
  //       basicSalary:
  //         formData.accountType === "permanent"
  //           ? parseFloat(formData.basicSalary)
  //           : null,
  //       rate:
  //         formData.accountType === "non_permanent"
  //           ? parseFloat(formData.rate)
  //           : null,
  //       rateType:
  //         formData.accountType === "non_permanent" ? formData.rateType : null,
  //       tenantId: userData.Tenant.id,
  //       supervisorId: formData.supervisorId
  //         ? parseInt(formData.supervisorId)
  //         : null,
  //       hasLogin: formData.hasLogin,
  //       scope: formData.scope,
  //       farms: farmsPayload,
  //       employeeIdsToUpdate: isSupervisorRole ? formData.workersIds : [],
  //     };

  //     if (customRoleData) {
  //       payload.roleId =
  //         customRoleData.roleId === 0 ? null : customRoleData.roleId;
  //       payload.roleNameEn = customRoleData.roleNameEn;
  //       payload.roleNameAr = customRoleData.roleNameAr;
  //       payload.permissions = customRoleData.permissions;
  //     } else {
  //       payload.roleId = formData.roleId ? parseInt(formData.roleId) : null;
  //     }

  //     try {
  //       await axios.post("/api/emp/newAddEmpToFarm", payload, {
  //         headers: { Authorization: token },
  //       });
  //       toast.success(
  //         t(
  //           isEditMode ? "User updated successfully!" : "User added successfully!"
  //         )
  //       );
  //       navigate("/employees");
  //     } catch (error) {
  //       toast.error(t("Failed to save user."));
  //       console.error("Save Error:", error.response?.data || error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  const formTitle = isEditMode
    ? t("Edit Employee")
    : isLaborer
    ? t("Add New Laborer")
    : t("Add New Employee");

  let roleDisplayName = "";
  if (customRoleData) {
    roleDisplayName =
      i18n.language === "ar"
        ? customRoleData.roleNameAr
        : customRoleData.roleNameEn;
  } else if (selectedRole) {
    roleDisplayName =
      i18n.language === "ar" ? selectedRole.nameAr : selectedRole.nameEn;
  }

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit}>
        <h1>{formTitle}</h1>

        {isLaborer ? (
          /********** ADD LABORER FORM **********/
          <>
            {/* ====== Identity & Role Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Name")}</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>{t("Role")}</label>
              <input type="text" value={t("Worker")} readOnly disabled />
            </div>

            <div className={styles.formGroup}>
              <label>{t("Assign Supervisor")}</label>
              <select
                name="supervisorId"
                value={formData.supervisorId}
                onChange={handleChange}
              >
                <option value="">{t("No Supervisor")}</option>
                {supervisors.map((supervisor) => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.username}
                  </option>
                ))}
              </select>
            </div>

            {/* ====== Account Creation Block ====== */}
            <div className={`${styles.formGroup} ${styles.toggleGroup}`}>
              <label htmlFor="hasLoginCheck">
                {t("Create a login account for this worker.")}
              </label>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  name="hasLogin"
                  checked={formData.hasLogin}
                  onChange={handleChange}
                  id="hasLoginCheck"
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            {/* Conditional Email/Password fields appear here if toggled */}
            {formData.hasLogin && (
              <>
                <div className={styles.formGroup}>
                  <label>{t("Email")}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required={formData.hasLogin}
                  />
                </div>
                <div
                  className={`${styles.formGroup} ${styles.passwordContainer}`}
                >
                  <label>{t("Password")}</label>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!isEditMode && formData.hasLogin}
                    className={styles.passwordInput}
                  />
                  <span
                    className={styles.passwordToggleIcon}
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </>
            )}

            {/* ====== Compensation Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Rate Type")}</label>
              <div className={styles.scopeSelector}>
                <button
                  type="button"
                  className={
                    formData.rateType === "hourly" ? styles.active : ""
                  }
                  onClick={() =>
                    setFormData((p) => ({ ...p, rateType: "hourly" }))
                  }
                >
                  {t("Hourly")}
                </button>
                <button
                  type="button"
                  className={formData.rateType === "daily" ? styles.active : ""}
                  onClick={() =>
                    setFormData((p) => ({ ...p, rateType: "daily" }))
                  }
                >
                  {t("Daily")}
                </button>
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>{t("Rate")}</label>
              <input
                type="number"
                name="rate"
                value={formData.rate}
                onChange={handleChange}
                required
                placeholder={
                  formData.rateType === "hourly"
                    ? t("Enter amount per hour")
                    : t("Enter amount per day")
                }
              />
            </div>

            {/* ====== Permissions Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Access Privilege")}</label>
              <div className={styles.scopeSelector}>
                <button
                  type="button"
                  className={formData.scope === "tenant" ? styles.active : ""}
                  onClick={() =>
                    setFormData((p) => ({ ...p, scope: "tenant" }))
                  }
                >
                  {t("All Farms")}
                </button>
                <button
                  type="button"
                  className={formData.scope === "farm" ? styles.active : ""}
                  onClick={() => setFormData((p) => ({ ...p, scope: "farm" }))}
                >
                  {t("This Farm")}
                </button>
                <button
                  type="button"
                  className={formData.scope === "field" ? styles.active : ""}
                  onClick={() => setFormData((p) => ({ ...p, scope: "field" }))}
                >
                  {t("Fields")}
                </button>
              </div>
            </div>

            {/* Conditional Field Selector appears here */}
            {formData.scope === "field" && (
              <div className={styles.formGroup}>
                <label>{t("Select Field(s)")}</label>
                <input
                  type="text"
                  readOnly
                  style={{ cursor: "pointer" }}
                  value={fields
                    .filter((field) => selectedFieldIds.includes(field.id))
                    .map((field) => field.name)
                    .join(", ")}
                  placeholder={t("Click to select fields")}
                  onClick={() =>
                    navigate("/select-fields", {
                      state: buildNavigationState({
                        allFields: fields,
                        initialSelectedIds: selectedFieldIds,
                        returnPath: location.pathname,
                      }),
                    })
                  }
                />
              </div>
            )}
          </>
        ) : (
          /********** ADD EMPLOYEE FORM **********/
          <>
            {/* ====== Existing Employee Toggle ====== */}
            {!isEditMode && (
              <div className={`${styles.formGroup} ${styles.toggleGroup}`}>
                <label htmlFor="existingUserToggle">
                  {t("Add an existing employee")}
                </label>
                <label className={styles.switch}>
                  <input
                    id="existingUserToggle"
                    type="checkbox"
                    checked={isExistingUser}
                    onChange={(e) => handleExistingUserToggle(e.target.checked)}
                  />
                  <span className={styles.slider}></span>
                </label>
              </div>
            )}

            {/* ====== Identity Block ====== */}
            {isExistingUser ? (
              <div className={styles.formGroup}>
                <label>{t("Select Employee")}</label>
                <select
                  name="existingEmployeeId"
                  value={formData.existingEmployeeId}
                  onChange={handleExistingUserSelect}
                  required
                >
                  <option value="">{t("Please select an employee")}</option>
                  {existingUsers.map((user) => (
                    <option key={user.Employee.id} value={user.Employee.id}>
                      {user.Employee.username}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className={styles.formGroup}>
                  <label>{t("Name")}</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </div>
                {/* Email and Password only for new, non-existing users */}
                {formData.hasLogin && !isExistingUser && (
                  <>
                    <div className={styles.formGroup}>
                      <label>{t("Email")}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required={formData.hasLogin}
                      />
                    </div>
                    <div
                      className={`${styles.formGroup} ${styles.passwordContainer}`}
                    >
                      <label>{t("Password")}</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required={!isEditMode && formData.hasLogin}
                        className={styles.passwordInput}
                      />
                      <span
                        className={styles.passwordToggleIcon}
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ====== Role & Responsibilities Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Role")}</label>
              <input
                type="text"
                value={
                  isEditMode
                    ? roleDisplayName || editModeRoleName
                    : roleDisplayName
                }
                placeholder={t("Select a Role")}
                onClick={() => {
                  navigate("/employees/set-role", {
                    state: buildNavigationState({
                      fromPath: location.pathname,
                      currentRoleId: formData.roleId,
                    }),
                  });
                }}
                readOnly
              />
            </div>

            {/* Conditional "Assign Workers" appears here */}
            {isSupervisorRole && (
              <div className={styles.formGroup}>
                <label>{t("Assign Workers to this Supervisor")}</label>
                <input
                  type="text"
                  readOnly
                  style={{ cursor: "pointer" }}
                  placeholder={t("Click to select workers")}
                  value={t("Select Workers")}
                  onClick={() =>
                    navigate("/select-workers", {
                      state: buildNavigationState({
                        allWorkers: workers,
                        initialSelectedIds: formData.workersIds,
                        returnPath: location.pathname,
                      }),
                    })
                  }
                />
                {selectedWorkers.length > 0 && (
                  <div className={styles.selectedWorkersList}>
                    {selectedWorkers.map((worker) => (
                      <div
                        key={worker.id}
                        className={styles.selectedWorkerItem}
                      >
                        <span className={styles.selectedWorkerName}>
                          {worker.username}
                        </span>
                        <button
                          type="button"
                          className={styles.deleteWorkerBtn}
                          onClick={() => handleRemoveWorker(worker.id)}
                        >
                          <DeleteIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ====== Compensation Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Basic Salary")}</label>
              <input
                type="number"
                name="basicSalary"
                value={formData.basicSalary}
                onChange={handleChange}
                required
              />
            </div>

            {/* ====== Permissions Block ====== */}
            <div className={styles.formGroup}>
              <label>{t("Access Privilege")}</label>
              <div className={styles.scopeSelector}>
                <button
                  type="button"
                  className={formData.scope === "tenant" ? styles.active : ""}
                  onClick={() =>
                    setFormData((p) => ({ ...p, scope: "tenant" }))
                  }
                >
                  {t("All Farms")}
                </button>
                <button
                  type="button"
                  className={formData.scope === "farm" ? styles.active : ""}
                  onClick={() => setFormData((p) => ({ ...p, scope: "farm" }))}
                >
                  {t("This Farm")}
                </button>
                <button
                  type="button"
                  className={formData.scope === "field" ? styles.active : ""}
                  onClick={() => setFormData((p) => ({ ...p, scope: "field" }))}
                >
                  {t("Fields")}
                </button>
              </div>
            </div>

            {/* Conditional Field Selector appears here */}
            {formData.scope === "field" && (
              <div className={styles.formGroup}>
                <label>{t("Select Field(s)")}</label>
                <input
                  type="text"
                  readOnly
                  style={{ cursor: "pointer" }}
                  value={fields
                    .filter((field) => selectedFieldIds.includes(field.id))
                    .map((field) => field.name)
                    .join(", ")}
                  placeholder={t("Click to select fields")}
                  onClick={() =>
                    navigate("/select-fields", {
                      state: buildNavigationState({
                        allFields: fields,
                        initialSelectedIds: selectedFieldIds,
                        returnPath: location.pathname,
                      }),
                    })
                  }
                />
              </div>
            )}
          </>
        )}

        {/* ====== Common Form Actions ====== */}
        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => navigate("/employees")}
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
          >
            {loading ? t("Saving...") : t("Save")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeForm;
